import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";
import { useForm } from "@tanstack/react-form";
import React, { Suspense, useEffect } from "react";

import HeadingLayout from "src/components/base/heading-layout";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import {
    ArrowPathRoundedSquareIcon,
    CheckCircleIcon,
    ClipboardDocumentIcon,
    ClockIcon,
    EllipsisVerticalIcon,
} from "@heroicons/react/20/solid";
import Form from "src/components/base/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import TablePagination from "src/components/table-pagination";
import { Badge } from "src/components/base/badges/base";

import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { Checkbox, CheckboxField } from "src/components/base/checkbox";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TFunction } from "i18next";

import { FullHeadscalePreAuthKey } from "src/api/generated/frontend_internal_v1";
import WS from "src/api/ws";
import ExpirePreAutKeyDialog from "src/components/dialogs/expire-pre-auth-key";
import { Text } from "src/components/base/text";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link PreAuthKeysOverview }
 */
export type PreAuthKeysOverviewProps = {};

const LIMIT = 25;

/**
 * An overview over all pre-auth keys
 */
function PreAuthKeysOverview(props: PreAuthKeysOverviewProps) {
    const [t] = useTranslation("admin-preauth-overview");
    const [tg] = useTranslation();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullHeadscalePreAuthKey>();
    const navigate = useNavigate();
    const router = useRouter();
    const { page, search, used, reusable, ephemeral, expired } = Route.useSearch();

    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.KeysChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <HeadingLayout
            heading={t("heading.preauth-overview")}
            headingDescription={t("description.preauth")}
            headingChildren={
                <PrimaryButton href={"/a/devices/create-server"}>{t("button.create-preauthkey")}</PrimaryButton>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/preauthkeys",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                            used: used ?? undefined,
                                            reusable: reusable ?? undefined,
                                            ephemeral: ephemeral ?? undefined,
                                            expired: expired ?? undefined,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Fieldset>
                    <div className={"grid h-full w-fit grid-cols-2 grid-rows-2 items-end justify-end gap-x-6"}>
                        <Field>
                            <CheckboxField title={t("label.ephemeral")}>
                                <Checkbox
                                    onChange={async (x) => {
                                        await navigate({
                                            to: "/a/preauthkeys",
                                            search: {
                                                page: 1,
                                                search,
                                                used,
                                                reusable,
                                                ephemeral: x,
                                                expired,
                                            },
                                        });
                                    }}
                                    color={"blue"}
                                    checked={ephemeral ?? false}
                                />

                                <div className={"flex items-center gap-2"}>
                                    <Text>
                                        <ClockIcon className={"size-4"} />
                                    </Text>
                                    <Label>{t("label.ephemeral")}</Label>
                                </div>
                            </CheckboxField>
                        </Field>
                        <Field>
                            <CheckboxField title={t("label.reusable")}>
                                <Checkbox
                                    checked={reusable ?? false}
                                    onChange={async (x) => {
                                        await navigate({
                                            to: "/a/preauthkeys",
                                            search: {
                                                page: 1,
                                                search,
                                                used,
                                                reusable: x,
                                                ephemeral,
                                                expired,
                                            },
                                        });
                                    }}
                                    color={"blue"}
                                />
                                <div className={"flex items-center gap-2"}>
                                    <Text>
                                        <ArrowPathRoundedSquareIcon className={"size-4"} />
                                    </Text>
                                    <Label>{t("label.reusable")}</Label>
                                </div>
                            </CheckboxField>
                        </Field>
                        <Field>
                            <CheckboxField>
                                <Checkbox
                                    checked={used ?? false}
                                    onChange={async (x) => {
                                        await navigate({
                                            to: "/a/preauthkeys",
                                            search: {
                                                page: 1,
                                                search,
                                                used: x,
                                                reusable,
                                                ephemeral,
                                                expired,
                                            },
                                        });
                                    }}
                                    color={"blue"}
                                    value="allow_embedding"
                                    title={t("label.used")}
                                />
                                <div className={"flex items-center gap-2"}>
                                    <Text>
                                        <CheckCircleIcon className={"size-4"} />
                                    </Text>
                                    <Label>{t("label.used")}</Label>
                                </div>
                            </CheckboxField>
                        </Field>
                        <Field>
                            <CheckboxField>
                                <Checkbox
                                    color={"blue"}
                                    checked={expired ?? false}
                                    onChange={async (x) => {
                                        await navigate({
                                            to: "/a/preauthkeys",
                                            search: {
                                                page: 1,
                                                search,
                                                used,
                                                reusable,
                                                ephemeral,
                                                expired: x,
                                            },
                                        });
                                    }}
                                    title={t("label.expired")}
                                />
                                <Label>{t("label.show-expired")}</Label>
                            </CheckboxField>
                        </Field>
                    </div>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.key")}</TableHeader>
                        <TableHeader>{t("label.expires")}</TableHeader>
                        <TableHeader></TableHeader>
                        <TableHeader>{t("label.tags")}</TableHeader>
                        <TableHeader>{t("label.description")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((key) => (
                        <TableRow key={key.uuid}>
                            <TableCell>
                                <div className={"flex items-center gap-1"}>
                                    <Button
                                        plain={true}
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(key.key);
                                            toast.success(tg("toast.copied-to-clipboard"));
                                        }}
                                    >
                                        <span className={"w-[8ch] overflow-hidden text-ellipsis"}>{key.key}</span>
                                        <ClipboardDocumentIcon />
                                    </Button>
                                </div>
                            </TableCell>
                            <TableCell>{key.expiration && displayExpiry(new Date(key.expiration), t)}</TableCell>
                            <TableCell>
                                <div className={"flex gap-6"}>
                                    {key.ephemeral ? (
                                        <ClockIcon
                                            title={t("label.ephemeral")}
                                            className={"size-4 fill-zinc-900 dark:fill-white"}
                                        />
                                    ) : (
                                        <ClockIcon
                                            title={t("label.ephemeral")}
                                            className={"size-4 fill-zinc-300 dark:fill-zinc-800"}
                                        />
                                    )}
                                    {key.reusable ? (
                                        <ArrowPathRoundedSquareIcon
                                            title={t("label.reusable")}
                                            className={"size-4 fill-zinc-900 dark:fill-white"}
                                        />
                                    ) : (
                                        <ArrowPathRoundedSquareIcon
                                            title={t("label.reusable")}
                                            className={"size-4 fill-zinc-300 dark:fill-zinc-800"}
                                        />
                                    )}
                                    {key.used ? (
                                        <CheckCircleIcon
                                            title={t("label.used")}
                                            className={"size-4 text-zinc-900 dark:text-white"}
                                        />
                                    ) : (
                                        <CheckCircleIcon
                                            title={t("label.used")}
                                            className={"size-4 text-zinc-300 dark:text-zinc-800"}
                                        />
                                    )}
                                </div>
                            </TableCell>

                            <TableCell>
                                <div className={"flex flex-wrap gap-1"}>
                                    {key.tags.map((tag) => (
                                        <TagBadge key={tag.uuid} tag={tag} />
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {key.description}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownHeading>{tg("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem onClick={() => setOpenExpireKey(key)}>
                                                <DropdownLabel>{t("button.set-expired")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/preauthkeys"}
                getSearchParams={(newPage: number) => ({ search, page: newPage, used, ephemeral, reusable, expired })}
            />

            {openExpireKey && (
                <Suspense>
                    <ExpirePreAutKeyDialog
                        preAuthKey={openExpireKey}
                        onClose={() => setOpenExpireKey(undefined)}
                        onExpire={() => {
                            router.invalidate({ sync: true });
                            setOpenExpireKey(undefined);
                        }}
                    />
                </Suspense>
            )}
        </HeadingLayout>
    );
}

/**
 * Display the time until expiration in different ways
 *
 * @param expiry The deadline to display
 * @param t for Translation
 *
 * @returns The rendered date
 */
function displayExpiry(expiry: Date, t: TFunction) {
    const now = new Date();
    dayjs.extend(relativeTime);
    const date = dayjs(expiry).fromNow();

    if (now.getTime() - expiry.getTime() > 0) {
        return <Badge color={"zinc"}>{t("label.expired").toLowerCase()}</Badge>;
    } else {
        return <Badge color={"blue"}>{date}</Badge>;
    }
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PreAuthKeysOverviewSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used to search trough descriptions */
    search: string;
    /** Filter by their ephemeral field */
    ephemeral?: boolean;
    /** Filter by their reusable field */
    reusable?: boolean;
    /** Filter by their reusable field */
    used?: boolean;
    /** Filter to show expired keys or not*/
    expired?: boolean;
};

export const Route = createFileRoute("/_admin/a/preauthkeys/")({
    component: PreAuthKeysOverview,
    validateSearch: (search: Record<string, unknown>): PreAuthKeysOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
            ephemeral: (search.ephemeral as boolean) || undefined,
            expired: (search.expired as boolean) || undefined,
            reusable: (search.reusable as boolean) || undefined,
            used: (search.used as boolean) || undefined,
        };
    },
    loaderDeps: ({ search: { page, search, used, reusable, ephemeral, expired } }) => ({
        page,
        search,
        used,
        reusable,
        ephemeral,
        expired,
    }),
    loader: async ({ deps }) =>
        await ApiInternal.preAuthKeys.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_description: deps.search,
            ephemeral: deps.ephemeral,
            reusable: deps.reusable,
            used: deps.used,
            expired: deps.expired ? undefined : false,
        }),
});
